/**
 * React hook for handling magic link authentication
 * Checks URL params for token and verifies it
 */

import * as React from 'react';
import { Dispatch } from 'redux';
import { verifyMagicLink } from '../api/authClient';
import { authManager } from '../api/authManager';
import { setUserEmail } from '../sync/authSyncSlice';
import logger from '../Analytics/Logger';

export interface UseMagicLinkAuthOptions {
  /** Redux dispatch function - if provided, will automatically dispatch setUserEmail */
  dispatch?: Dispatch;
  
  /** Callback when authentication succeeds (called after auto-dispatch if both provided) */
  onSuccess?: (email: string) => void;
  
  /** Callback when authentication fails */
  onError?: (error: Error) => void;
  
  /** 
   * Callback after successful token verification to navigate to main game.
   * If provided, will be called after a short delay to allow success message to show.
   * Useful for redirecting from callback page back to game.
   */
  onSuccessNavigate?: () => void;
}

export interface UseMagicLinkAuthResult {
  /** Whether currently verifying the magic link */
  isVerifying: boolean;
  
  /** Email if already authenticated */
  userEmail: string | null;
  
  /** Error if verification failed (can be used to show PageAuthError component) */
  error: Error | null;
  
  /** Error type for displaying appropriate error message ('invalid' | 'expired') */
  errorType: 'invalid' | 'expired' | null;
}

/**
 * Hook to handle magic link authentication from URL params
 * 
 * Usage:
 * ```tsx
 * // With auto-dispatch (recommended)
 * const { isVerifying, error, errorType } = useMagicLinkAuth({ dispatch });
 * 
 * // Show error page if verification failed
 * if (error && errorType) {
 *   return <PageAuthError errorType={errorType} />;
 * }
 * 
 * // With manual callback
 * const { isVerifying } = useMagicLinkAuth({
 *   onSuccess: (email) => console.log(email),
 *   onError: (err) => console.error(err)
 * });
 * ```
 */
export function useMagicLinkAuth(options: UseMagicLinkAuthOptions = {}): UseMagicLinkAuthResult {
  const { dispatch, onSuccess, onError, onSuccessNavigate } = options;
  const [isVerifying, setIsVerifying] = React.useState(false);
  const [userEmail, setUserEmailState] = React.useState<string | null>(null);
  const [error, setError] = React.useState<Error | null>(null);
  const [errorType, setErrorType] = React.useState<'invalid' | 'expired' | null>(null);
  
  // Ref to prevent double verification in StrictMode
  const isVerifyingRef = React.useRef(false);
  
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    
    // Prevent double verification if already in progress (StrictMode protection)
    if (isVerifyingRef.current) {
      logger.debug('[Magic Link Auth] Verification already in progress, skipping duplicate call');
      return;
    }
    
    // Check if already authenticated (prevents double verification in StrictMode)
    const currentUser = authManager.getCurrentUser();
    
    if (!token) {
      // No token in URL - check if already authenticated from previous session
      if (currentUser) {
        setUserEmailState(currentUser.email);
        
        // Auto-dispatch to Redux if dispatch provided
        if (dispatch) {
          dispatch(setUserEmail(currentUser.email));
        }
        
        // Call success callback with existing session
        if (onSuccess) {
          onSuccess(currentUser.email);
        }
      }
      return;
    }
    
    // Token exists - check if we're already authenticated
    // If auth_session cookie exists, the token was already verified successfully
    if (currentUser) {
      logger.debug('[Magic Link Auth] Already authenticated, skipping token verification');
      setUserEmailState(currentUser.email);
      
      // Remove token from URL
      window.history.replaceState({}, document.title, window.location.pathname);
      
      if (dispatch) {
        dispatch(setUserEmail(currentUser.email));
      }
      
      if (onSuccess) {
        onSuccess(currentUser.email);
      }
      
      if (onSuccessNavigate) {
        setTimeout(() => {
          onSuccessNavigate();
        }, 1500);
      }
      return;
    }
    
    // Not authenticated - verify the token
    isVerifyingRef.current = true;
    setIsVerifying(true);
    
    verifyMagicLink(token)
      .then((response) => {
        // Remove token from URL
        window.history.replaceState({}, document.title, window.location.pathname);
        isVerifyingRef.current = false;
        setIsVerifying(false);
        setUserEmailState(response.email);
        
        // Auto-dispatch to Redux if dispatch provided
        if (dispatch) {
          dispatch(setUserEmail(response.email));
        }
        
        // Call success callback
        if (onSuccess) {
          onSuccess(response.email);
        }
        
        // Navigate after success if callback provided (for callback pages)
        if (onSuccessNavigate) {
          // Small delay to show success message before navigating
          setTimeout(() => {
            onSuccessNavigate();
          }, 2000);
        }
        
        logger.log('[Magic Link Auth] Successfully logged in:', response.email);
      })
      .catch((err) => {
        logger.error('[Magic Link Auth] Verification failed:', err);
        isVerifyingRef.current = false;
        setIsVerifying(false);
        setError(err);
          
          // Determine error type for UI display
          const type = err.message.includes('expired') ? 'expired' : 'invalid';
          setErrorType(type);
          
          // Call error callback
          if (onError) {
            onError(err);
          }
        });
  }, [dispatch, onSuccess, onError, onSuccessNavigate]);
  
  return {
    isVerifying,
    userEmail,
    error,
    errorType,
  };
}
